/**
 * `/browse/genres` — TASK 10.3 Step 1.
 *
 * The mock's `_browse(V,'genres')` view model and its template, wired to
 * `GET /api/genres` (TASK 4.3) instead of the in-memory `db.genres`. The
 * shape deliberately mirrors ../browse/ArtistsList.js — the two are the same
 * Browse surface behind the same tab strip — with the genre-specific
 * differences called out below:
 *
 *   - Rows are `link:genre-row →#/genres/*` (ui-contract §3, ×16): name +
 *     album count, NO initials thumb (the mock's genre row renders
 *     "Alternative Rock 469 albums" plain — compare the artist row's
 *     "BA Broken Antenna 4 albums").
 *   - `albumCount` is the album-genre UNION computed server-side at query
 *     time (dto.ts#genreListItemSchema): an album counts once if ANY of its
 *     tracks carries the genre.
 *
 * ── AC 15.1: ALPHA WITH `Unknown` LAST — THE SERVER SORTS ─────────────────
 * routes/genres.ts: "All genres alphabetically … the reserved `Unknown`
 * genre is forced LAST regardless of alphabet". The client does NOT re-sort,
 * for the same reason ArtistDetail.js documents: the order is global across
 * keyset pages, so sorting each arriving page locally would interleave a
 * later page's early letters back above an earlier page's late ones — and
 * would drag `Unknown` out of its contract-mandated last place whenever it
 * arrives before the final page. Order preservation IS the correctness
 * property here, and the suite asserts it.
 *
 * ── AC 15.5: BOUNDED ──────────────────────────────────────────────────────
 * ../../components/VirtualList.js owns BOTH bounds (render window and fetch).
 * The bench profile is 16 genres — under one window — but the endpoint is
 * keyset-paginated (`dto.ts#cursorPageFields`), so the pagination affordance
 * exists and derives its id from its own accessible name per the contract's
 * rule ("Load more genres ↓" → `load-more-genres-button`), exactly as
 * `load-more-artists-button` derives.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 10.3's files */

import { genreRowLink } from '@streamymusic/shared';

import { controlTestId } from '../../app/testids.js';
import { VirtualList } from '../../components/VirtualList.js';
import {
  EmptyBlock,
  ErrorBlock,
  LIBRARY_PREPARING_MESSAGE,
  LoadingBlock,
} from '../../components/StateBlocks.js';
import { append, el } from '../../components/primitives/dom.js';

import { BrowseTabs } from '../browse/BrowseTabs.js';

/** ui-contract §10.6 scale table — row lists render 40, then +40. */
export const GENRES_PAGE_SIZE = 40;

/** The pagination control's accessible name — its testid derives from it. */
export const LOAD_MORE_GENRES_LABEL = 'Load more genres ↓';

/**
 * One row of the genre list (`link:genre-row →#/genres/*`, ui-contract §3).
 *
 * @param {import('@streamymusic/shared').GenreListItem} genre
 * @returns {HTMLElement}
 */
export function GenreRow(genre) {
  const row = el('a', {
    className: 'sm-row',
    testId: genreRowLink(genre.id),
    attrs: { href: `#/genres/${encodeURIComponent(genre.id)}` },
  });
  append(
    row,
    el('span', { className: 'sm-line-name', text: genre.name }),
    el('span', {
      className: 'sm-line-trailing',
      text: `${genre.albumCount.toLocaleString()} album${genre.albumCount === 1 ? '' : 's'}`,
    }),
  );
  return row;
}

/**
 * Builds the `/browse/genres` section and starts loading its first page.
 *
 * @param {{
 *   api: {
 *     getGenres: (
 *       options?: import('../../../api/api-client.js').PageOptions,
 *     ) => Promise<import('@streamymusic/shared').GenreListResponse>,
 *   },
 *   heading: HTMLElement,
 *   isAdmin?: boolean,
 *   pageSize?: number,
 *   depth?: import('../../../lib/list-depth.js').ListDepth,
 *   announce?: (text: string) => void,
 * }} options
 *   `api` is typed structurally rather than `Pick<ApiClient, 'getGenres'>`
 *   because the client method lands with this task's wiring — the structural
 *   type IS the signature the api-client must satisfy (`GET /api/genres`,
 *   parsed by `genreListResponseSchema`, shaped exactly like `getArtists`).
 *   `heading` is the route's `<h1 data-testid="page-heading">` (router.js
 *   `PageHeading`) — the shell contract requires exactly one per route and
 *   the route announcer focuses it, so the view is handed it rather than
 *   making its own. `announce` is the shell's persistent polite live region
 *   (LiveRegions.js) — see StateBlocks.js#ErrorBlock for why the blocks
 *   cannot announce themselves.
 * @returns {{ element: HTMLElement, dispose: () => void }} the `<section>` for
 *   `Shell#renderRoute`, plus the cancel hook the composition root calls on
 *   the next navigation
 */
export function GenresList(options) {
  const { api, heading, isAdmin = false, pageSize = GENRES_PAGE_SIZE, depth, announce } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Browse' } });

  const tabs = BrowseTabs('genres');

  const countLabel = el('p', { className: 'sm-count-label', attrs: { hidden: true } });
  const body = el('div');
  /** Holds an error for a FAILED "load more" — the loaded rows must survive it. */
  const notice = el('div');
  append(section, heading, tabs, countLabel, body, notice);

  /** @type {import('@streamymusic/shared').GenreListItem[]} */
  let items = [];
  /** @type {string|null} */
  let cursor = null;
  /**
   * Guards against an out-of-order settle: a Retry issued while the previous
   * request is still in flight would otherwise paint the older answer.
   */
  let generation = 0;
  let disposed = false;
  /** Cancels the in-flight page fetch on dispose (and on a superseding load). */
  /** @type {AbortController|null} */
  let controller = null;
  /** Pages held, so the depth memory can replay them on return (#back). */
  let pagesLoaded = 0;

  /** The depth this route was left at, read ONCE before the first paint. */
  const restored = depth?.read() ?? null;

  const list = VirtualList({
    items,
    windowSize: pageSize,
    renderItem: (genre) => GenreRow(genre),
    loadMoreLabel: LOAD_MORE_GENRES_LABEL,
    // Derived with the contract's own rule (kebab-case of the accessible
    // name), never hand-written: `load-more-genres-button`.
    loadMoreTestId: controlTestId(LOAD_MORE_GENRES_LABEL),
    listClassName: 'sm-stack-8',
    ...(restored === null ? {} : { initialShown: restored.shown }),
    onWindowChange: (shown) => depth?.remember(pagesLoaded, shown),
    onLoadMore: () => {
      void load({ append: true });
    },
  });

  /**
   * The count line. The endpoint returns no TOTAL (it is a keyset page, not
   * an offset page), so the honest label is what we hold — with a "+" while
   * the server says there is more.
   */
  function paintCount() {
    const n = items.length;
    countLabel.textContent = `${n.toLocaleString()}${cursor === null ? '' : '+'} genre${n === 1 ? '' : 's'}`;
    countLabel.removeAttribute('hidden');
  }

  /**
   * @param {{ append?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const own = (generation += 1);
    // A superseded request is CANCELLED, not merely ignored: the generation
    // guard already discards its settle, aborting stops the transfer too.
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (!appendPage) {
      items = [];
      cursor = null;
      pagesLoaded = 0;
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 6, label: 'Loading genres' }));
    }

    try {
      const page = await api.getGenres({
        limit: pageSize,
        ...(appendPage && cursor !== null ? { cursor } : {}),
        signal: ownController.signal,
      });
      if (disposed || own !== generation) return;

      // Server order preserved verbatim (AC 15.1 — see the module doc).
      items = appendPage ? [...items, ...page.items] : [...page.items];
      cursor = page.nextCursor;
      pagesLoaded += 1;

      if (items.length === 0) {
        body.replaceChildren(
          EmptyBlock({
            message: LIBRARY_PREPARING_MESSAGE,
            showSyncLink: isAdmin,
            ...(announce === undefined ? {} : { announce }),
          }),
        );
        countLabel.setAttribute('hidden', '');
        return;
      }

      paintCount();
      list.update({ items, hasMore: cursor !== null });
      if (body.firstChild !== list.element) body.replaceChildren(list.element);
      depth?.remember(pagesLoaded, list.shownCount());
    } catch {
      if (disposed || own !== generation) return;
      // The api-client has already collapsed the failure to a SAFE message and
      // handled 401/403 globally; a browse read has nothing user-specific to
      // add, so this is the shared retriable card.
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage) {
        // A failed NEXT page must not discard the pages already on screen.
        list.update({ busy: false });
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  /**
   * First paint, re-opening the depth the user left (#back). Every replayed
   * page is a hit on the api-client's 24h per-cursor catalog cache, so this
   * costs no network — see `lib/list-depth.ts`.
   *
   * @returns {Promise<void>}
   */
  async function loadToRememberedDepth() {
    await load();
    if (restored === null) return;
    for (let page = 1; page < restored.pages; page += 1) {
      if (disposed || cursor === null) return;
      const before = items.length;
      await load({ append: true });
      if (items.length === before) return; // page failed or added nothing
    }
  }

  void loadToRememberedDepth();

  return {
    element: section,
    dispose() {
      // Cancels every in-flight paint: the next settle sees `disposed` and
      // writes nothing into a section the shell has already replaced. The
      // abort additionally cancels the TRANSFER — no orphaned fetch keeps
      // downloading a page nobody will render.
      disposed = true;
      controller?.abort();
      list.dispose();
    },
  };
}
